import { useState } from "react";
import { Card, CardContent } from "@component/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@component/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import { EyeIcon, MonitorIcon, SmartphoneIcon, StarIcon } from "lucide-react";
import PreviewInterviewCardHeader from "@component/preview-waiting-room/preview-interview-card-header";
import PreviewVideoSection from "@component/preview-waiting-room/preview-video-section";
import PreviewInterviewSidebar from "@component/preview-waiting-room/preview-interview-sidebar";
import { ProgressBar } from "@component/progress-bar";

interface DemoPreviewDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    logoAPreviews: string | null;
    logoBPreviews: string | null;
    companyName: string;
    companyColor: string;
    children: React.ReactNode;
}

export function DemoPreviewDialog({
    open,
    onOpenChange,
    logoAPreviews,
    logoBPreviews,
    companyName,
    companyColor,
    children,
}: DemoPreviewDialogProps) {
    const [selectedLogo, setSelectedLogo] = useState<"logoA" | "logoB">("logoA");
    const [selectedScreen, setSelectedScreen] = useState<
        "landing" | "interview" | "rating"
    >("landing");

    const hasLogos = logoAPreviews && logoBPreviews;
    const currentLogo = selectedLogo === "logoA" ? logoAPreviews : logoBPreviews;

    const renderLandingScreen = () => (
        <Card className="border-2 border-gray-200 overflow-hidden">
            <CardContent className="p-0">
                <div className="w-full h-[500px] overflow-x-scroll bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 relative flex flex-col">
                    {/* Background decorative elements */}
                    <div className="absolute inset-0 overflow-hidden pointer-events-none">
                        <div className="absolute top-10 left-5 w-32 h-32 bg-blue-100 rounded-full mix-blend-multiply filter blur-xl opacity-30"></div>
                        <div className="absolute top-20 right-5 w-32 h-32 bg-purple-100 rounded-full mix-blend-multiply filter blur-xl opacity-30"></div>
                        <div className="absolute bottom-10 left-1/3 w-32 h-32 bg-pink-100 rounded-full mix-blend-multiply filter blur-xl opacity-30"></div>
                    </div>

                    {/* Header Section */}
                    <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 w-full py-8 px-4 rounded-b-3xl shadow-xl relative z-10">
                        {/* Logo */}
                        <div className="flex justify-center mb-4">
                            {logoAPreviews ? (
                                <img
                                    src={logoAPreviews}
                                    alt={`${companyName} preview`}
                                    className="h-16 w-auto object-contain"
                                />
                            ) : (
                                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                                    <span className="text-black font-bold text-sm">LOGO</span>
                                </div>
                            )}
                        </div>

                        <h1 className="text-2xl font-bold text-white text-center tracking-tight">
                            AI Interviewer Demo
                        </h1>
                        <p className="text-gray-300 text-center mt-2 text-sm">
                            A personalized demo for {companyName}.
                        </p>
                    </div>

                    {/* Form Section */}
                    <div className="flex-1 flex items-center justify-center px-4 -mt-4 relative z-20">
                        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md border">
                            <div className="text-center mb-4">
                                <p className="text-sm mb-4">
                                    This demo showcases how ourteam’s AI Interview works.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Your Name
                                    </label>
                                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                                        Your name
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Email
                                    </label>
                                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                                        your.email@example.com
                                    </div>
                                </div>

                                <div className="text-center text-sm text-gray-600 leading-relaxed">
                                    <p>
                                        Your data is kept private and will not be shared with anyone.
                                        <br />
                                        For enquiries, contact <span className="underline">hello@ourteam.ai</span>.
                                    </p>
                                </div>

                                <button className="w-full bg-black text-white py-2 px-4 rounded-full font-medium transition-colors">
                                    Proceed
                                </button>
                            </div>
                            <div className="text-center mt-6">
                                <img
                                    alt="Powered by ourteam"
                                    className="mx-auto h-12 w-auto opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                    src="https://assets.polymet.ai/nervous-tan-828931"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    const renderInterviewScreen = () => (
        <Card className="border-2 border-gray-200 overflow-hidden">
            <CardContent className="p-0">
                <div className="w-full h-full bg-white flex flex-col">
                    {/* Company Logo */}
                    <div className="flex justify-center py-4">
                        {logoBPreviews && (
                            <img
                                src={logoBPreviews}
                                alt={`${companyName} preview`}
                                className="h-16 w-auto object-contain"
                            />
                        )}
                    </div>

                    {/* Main Interview Interface */}
                    <div className="flex-1 px-4 pb-4">
                        <div className="h-full flex flex-col">
                            {/* Interview Card */}
                            <div className="max-w-5xl mx-auto w-full bg-white overflow-hidden mt-4">
                                <PreviewInterviewCardHeader />

                                {/* Interview Content */}
                                <div className="flex gap-10">
                                    <PreviewVideoSection />

                                    <PreviewInterviewSidebar
                                        accentColor={companyColor}
                                        showBranding={true}
                                        timeRemaining={"01:29"}
                                    />
                                </div>
                                <div className="mt-10">
                                    <ProgressBar
                                        currentStep={1}
                                        steps={[
                                            {
                                                label: "AI Interview",
                                                isCompleted: false,
                                                isActive: true,
                                            },
                                            {
                                                label: "Complete",
                                                isCompleted: false,
                                                isActive: false,
                                            },
                                        ]}
                                        isShowBanding={true}
                                        companyPreferences={{
                                            primary_color: companyColor,
                                            company_logo: "",
                                            ai_bot_logo: "",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card >
    );

    const renderRatingScreen = () => (
        <Card className="border-2 border-gray-200 overflow-hidden">
            <CardContent className="p-0">
                <div className="max-w-2xl h-full flex flex-col mx-auto">
                    {/* Company Logo */}
                    <div className="flex justify-center py-4">
                        {logoBPreviews ? (
                            <img
                                src={logoBPreviews}
                                alt={`${companyName} preview`}
                                className="h-16 w-auto object-contain"
                            />
                        ) : (
                            <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-bold text-sm">AI</span>
                            </div>
                        )}
                    </div>

                    {/* Rating Interface */}
                    <div className="flex-1 px-4 pb-4">
                        <div className="border border-gray-100 rounded-lg p-6 h-full flex flex-col justify-center">
                            <div className="text-center mb-8">
                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    How was your interview experience?
                                </h2>
                                <p className="text-gray-600">
                                    Your feedback helps us improve our AI interview experience.
                                </p>
                            </div>

                            {/* Star Rating */}
                            <div className="flex justify-center mb-8">
                                <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            className="text-yellow-400 hover:text-yellow-500 transition-colors"
                                        >
                                            <StarIcon className="h-8 w-8 fill-current" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Feedback Text */}
                            <div className="mb-6">
                                <h4 className="text-base font-medium text-gray-800 mb-2" style={{ marginTop: 10, paddingTop: 0, paddingBottom: 0 }}>
                                    Did you face any issues?
                                </h4>
                                <div className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-500 text-sm">
                                    Share your thoughts about the interview experience...
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3">
                                <button className="flex-1 bg-blue-600 text-white py-2 px-2 rounded-md font-medium text-sm hover:bg-blue-700 transition-colors">
                                    Submit Feedback
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </Card>
    );

    const renderEmptyState = () => (
        <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
                <EyeIcon className="h-12 w-12 mx-auto mb-3" />
            </div>
            <p className="text-gray-500 text-lg">
                Upload at least one logo to see the AI Interviewer Demo preview.
            </p>
            <p className="text-gray-400 text-sm mt-2">
                The preview will show how your demo will look with each logo variation.
            </p>
        </div>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Company Demo Preview</DialogTitle>
                </DialogHeader>

                {!hasLogos ? (
                    renderEmptyState()
                ) : (
                    <div className="space-y-6">
                        {/* Screen Selection Tabs */}
                        <Tabs
                            value={selectedScreen}
                            onValueChange={(value) =>
                                setSelectedScreen(value as "landing" | "interview" | "rating")
                            }
                        >
                            <TabsList className="grid w-full grid-cols-3">
                                <TabsTrigger
                                    value="landing"
                                    className="flex items-center gap-2"
                                >
                                    <SmartphoneIcon className="h-4 w-4" />
                                    Landing Screen
                                </TabsTrigger>
                                <TabsTrigger
                                    value="interview"
                                    className="flex items-center gap-2"
                                >
                                    <MonitorIcon className="h-4 w-4" />
                                    Interview Screen
                                </TabsTrigger>
                                <TabsTrigger value="rating" className="flex items-center gap-2">
                                    <StarIcon className="h-4 w-4" />
                                    Feedback Screen
                                </TabsTrigger>
                            </TabsList>

                            <TabsContent value="landing">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            Demo Landing Screen
                                        </h3>
                                    </div>
                                    {currentLogo ? (
                                        renderLandingScreen()
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            No {selectedLogo === "logoA" ? "Logo A" : "Logo B"}{" "}
                                            uploaded
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="interview" className="mt-6">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            Interview Screen
                                        </h3>
                                    </div>
                                    {currentLogo ? (
                                        renderInterviewScreen()
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            No {selectedLogo === "logoA" ? "Logo A" : "Logo B"}{" "}
                                            uploaded
                                        </div>
                                    )}
                                </div>
                            </TabsContent>

                            <TabsContent value="rating" className="mt-6">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-semibold text-gray-900">
                                            Feedback Screen
                                        </h3>
                                    </div>
                                    {currentLogo ? (
                                        renderRatingScreen()
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            No {selectedLogo === "logoA" ? "Logo A" : "Logo B"}{" "}
                                            uploaded
                                        </div>
                                    )}
                                </div>
                            </TabsContent>
                        </Tabs>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
